import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Database, 
  Download, 
  Upload,
  CheckCircle,
  AlertTriangle,
  Clock,
  HardDrive,
  Shield,
  RefreshCw,
  Archive,
  Calendar,
  FileDown,
  Settings,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { adminService, type Backup as BackupType, type BackupSchedule as BackupScheduleType, type DatabaseTable } from '@/services/adminService';

// Use types from adminService
type Backup = BackupType;
type BackupSchedule = BackupScheduleType;

export const BackupRestore: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [schedule, setSchedule] = useState<BackupSchedule | null>(null);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<Backup | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [selectedTables, setSelectedTables] = useState<string[]>([
    'patients', 'chd_diagnoses', 'health_markers', 'procedures', 'maternal_data'
  ]);
  const [availableTables, setAvailableTables] = useState<DatabaseTable[]>([]);

  useEffect(() => {
    loadBackups();
    loadBackupSchedule();
    loadDatabaseTables();
  }, []);

  const loadBackups = async () => {
    try {
      const backupsList = await adminService.listBackups();
      setBackups(backupsList);
      setLoading(false);
    } catch (err) {
      console.error('Error loading backups:', err);
      setBackups([]);
      setLoading(false);
    }
  };

  const loadBackupSchedule = async () => {
    try {
      const scheduleData = await adminService.getBackupSchedule();
      setSchedule(scheduleData);
    } catch (err) {
      console.error('Error loading backup schedule:', err);
      // Set default schedule if API fails
      setSchedule({
        enabled: true,
        frequency: 'daily',
        time: '02:00',
        retention_days: 30,
        next_run: new Date(Date.now() + 86400000).toISOString()
      });
    }
  };

  const loadDatabaseTables = async () => {
    try {
      const tables = await adminService.getDatabaseTables();
      setAvailableTables(tables);
      
      // Pre-select important tables if they exist
      const importantTables = ['patients', 'chd_diagnoses', 'health_markers', 'procedures', 'maternal_data'];
      const existingTables = tables.filter(t => importantTables.includes(t.name)).map(t => t.name);
      if (existingTables.length > 0) {
        setSelectedTables(existingTables);
      }
    } catch (err) {
      console.error('Error loading database tables:', err);
      // Set default tables if API fails
      setAvailableTables([
        { name: 'patients', label: 'Pacienți', records: 0, size_mb: 0 },
        { name: 'chd_diagnoses', label: 'Diagnostice MCC', records: 0, size_mb: 0 },
        { name: 'health_markers', label: 'Markeri sănătate', records: 0, size_mb: 0 },
        { name: 'procedures', label: 'Proceduri', records: 0, size_mb: 0 },
        { name: 'maternal_data', label: 'Date maternale', records: 0, size_mb: 0 }
      ]);
    }
  };

  const createBackup = async () => {
    setBackupInProgress(true);
    setBackupProgress(0);
    
    // Show progress animation
    const interval = setInterval(() => {
      setBackupProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const result = await adminService.createBackup();
      clearInterval(interval);
      setBackupProgress(100);
      
      // Add the new backup to the list
      const newBackup: Backup = {
        id: Date.now(),
        filename: result.backup_file,
        size_mb: 0, // Size will be determined by backend
        type: 'manual',
        status: 'completed',
        created_at: new Date().toISOString(),
        created_by: 'Current User',
        tables_included: selectedTables,
        compression: true,
        encrypted: true
      };
      setBackups([newBackup, ...backups]);
      // Reload backups from server to get accurate data
      setTimeout(() => loadBackups(), 1000);
      
      setTimeout(() => {
        setBackupInProgress(false);
        setBackupProgress(0);
      }, 1000);
    } catch (err: any) {
      clearInterval(interval);
      console.error('Error creating backup:', err);
      alert(err.message || 'Eroare la crearea backup-ului');
      setBackupInProgress(false);
      setBackupProgress(0);
    }
  };

  const restoreBackup = async (backup: Backup) => {
    setRestoreInProgress(true);
    setShowRestoreDialog(false);
    
    // Simulate restore process
    setTimeout(() => {
      setRestoreInProgress(false);
      alert(`Backup ${backup.filename} restaurat cu succes!`);
    }, 3000);
  };

  const downloadBackup = async (backup: Backup) => {
    try {
      await adminService.downloadBackup(backup.id, backup.filename);
    } catch (err) {
      console.error('Error downloading backup:', err);
      alert('Eroare la descărcarea backup-ului');
    }
  };

  const deleteBackup = async (backup: Backup) => {
    if (window.confirm(`Sigur doriți să ștergeți backup-ul ${backup.filename}?`)) {
      try {
        await adminService.deleteBackup(backup.id);
        setBackups(backups.filter(b => b.id !== backup.id));
      } catch (err) {
        console.error('Error deleting backup:', err);
        alert('Eroare la ștergerea backup-ului');
      }
    }
  };

  const getStatusBadge = (status: Backup['status']) => {
    const config = {
      completed: { variant: 'default' as const, icon: CheckCircle, label: 'Complet', className: 'bg-green-500' },
      in_progress: { variant: 'secondary' as const, icon: Clock, label: 'În progres', className: '' },
      failed: { variant: 'destructive' as const, icon: AlertTriangle, label: 'Eșuat', className: '' }
    };
    const { variant, icon: Icon, label, className } = config[status];
    return (
      <Badge variant={variant} className={className}>
        <Icon className="mr-1 h-3 w-3" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32" />
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Backup & Restore</h1>
            <p className="text-muted-foreground">
              Gestionează copiile de siguranță ale bazei de date
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline">
              <Settings className="mr-2 h-4 w-4" />
              Configurări
            </Button>
            <Button onClick={createBackup} disabled={backupInProgress}>
              {backupInProgress ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Backup în progres...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Crează Backup
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Backup Progress */}
        {backupInProgress && (
          <Alert>
            <Database className="h-4 w-4" />
            <AlertTitle>Backup în progres</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-2">Se creează copia de siguranță a bazei de date...</p>
              <Progress value={backupProgress} className="w-full" />
              <p className="text-sm text-muted-foreground mt-2">{backupProgress}% completat</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Restore Progress */}
        {restoreInProgress && (
          <Alert>
            <RefreshCw className="h-4 w-4 animate-spin" />
            <AlertTitle>Restaurare în progres</AlertTitle>
            <AlertDescription>
              Se restaurează baza de date din backup. Vă rugăm să nu închideți această pagină.
            </AlertDescription>
          </Alert>
        )}

        {/* Statistics */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total backup-uri</CardTitle>
              <Archive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{backups.length}</div>
              <p className="text-xs text-muted-foreground">Disponibile pentru restaurare</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Spațiu utilizat</CardTitle>
              <HardDrive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {backups.reduce((sum, b) => sum + b.size_mb, 0).toFixed(1)} MB
              </div>
              <p className="text-xs text-muted-foreground">Total stocare backup-uri</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ultimul backup</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {backups[0] ? format(new Date(backups[0].created_at), 'HH:mm', { locale: ro }) : '-'}
              </div>
              <p className="text-xs text-muted-foreground">
                {backups[0] ? format(new Date(backups[0].created_at), 'dd MMM yyyy', { locale: ro }) : 'Nu există'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Backup automat</CardTitle>
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {schedule?.enabled ? 'Activ' : 'Inactiv'}
              </div>
              <p className="text-xs text-muted-foreground">
                Următorul: {schedule?.enabled && schedule.next_run ? format(new Date(schedule.next_run), 'dd MMM HH:mm', { locale: ro }) : '-'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Backup Schedule */}
        {schedule && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Programare backup automat</span>
              <Button
                variant={schedule.enabled ? 'default' : 'outline'}
                size="sm"
                onClick={async () => {
                  const updatedSchedule = {...schedule, enabled: !schedule.enabled};
                  setSchedule(updatedSchedule);
                  try {
                    await adminService.updateBackupSchedule(updatedSchedule);
                  } catch (err) {
                    console.error('Error updating backup schedule:', err);
                    // Revert on error
                    setSchedule(schedule);
                  }
                }}
              >
                {schedule.enabled ? (
                  <>
                    <Pause className="mr-2 h-4 w-4" />
                    Dezactivează
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Activează
                  </>
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <label className="text-sm font-medium">Frecvență</label>
                <p className="text-lg">{schedule.frequency === 'daily' ? 'Zilnic' : schedule.frequency === 'weekly' ? 'Săptămânal' : 'Lunar'}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Ora</label>
                <p className="text-lg">{schedule.time}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Păstrare</label>
                <p className="text-lg">{schedule.retention_days} zile</p>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <div className="mt-1">
                  {schedule.enabled ? (
                    <Badge className="bg-green-500">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Activ
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Inactiv</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Table Selection for Backup */}
        <Card>
          <CardHeader>
            <CardTitle>Selectare tabele pentru backup</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
              {availableTables.map((table) => (
                <div key={table.name} className="flex items-center space-x-2">
                  <Checkbox
                    id={table.name}
                    checked={selectedTables.includes(table.name)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedTables([...selectedTables, table.name]);
                      } else {
                        setSelectedTables(selectedTables.filter(t => t !== table.name));
                      }
                    }}
                  />
                  <label
                    htmlFor={table.name}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {table.label} ({table.records.toLocaleString()} înregistrări)
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Backups Table */}
        <Card>
          <CardHeader>
            <CardTitle>Istoric backup-uri</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume fișier</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dimensiune</TableHead>
                  <TableHead>Tabele</TableHead>
                  <TableHead>Securitate</TableHead>
                  <TableHead>Creat</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell className="font-mono text-sm">{backup.filename}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {backup.type === 'manual' ? 'Manual' : backup.type === 'scheduled' ? 'Programat' : 'Automat'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(backup.status)}</TableCell>
                    <TableCell>{backup.size_mb.toFixed(1)} MB</TableCell>
                    <TableCell>
                      {backup.tables_included[0] === 'all' ? (
                        <Badge>Toate</Badge>
                      ) : (
                        <Badge variant="secondary">{backup.tables_included.length} tabele</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {backup.compression && (
                          <Badge variant="outline" className="text-xs">
                            Comprimat
                          </Badge>
                        )}
                        {backup.encrypted && (
                          <Badge variant="outline" className="text-xs">
                            <Shield className="mr-1 h-3 w-3" />
                            Criptat
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {format(new Date(backup.created_at), 'dd MMM yyyy', { locale: ro })}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(backup.created_at), 'HH:mm', { locale: ro })} - {backup.created_by}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedBackup(backup);
                            setShowRestoreDialog(true);
                          }}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadBackup(backup)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteBackup(backup)}
                        >
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Restore Dialog */}
        <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Restaurare backup</DialogTitle>
              <DialogDescription>
                Această acțiune va înlocui toate datele curente cu cele din backup.
                Asigurați-vă că ați creat un backup recent înainte de a continua.
              </DialogDescription>
            </DialogHeader>
            {selectedBackup && (
              <div className="space-y-4">
                <div className="rounded-lg border p-4">
                  <p className="text-sm font-medium">Detalii backup:</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fișier: {selectedBackup.filename}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Creat: {format(new Date(selectedBackup.created_at), 'dd MMM yyyy HH:mm', { locale: ro })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Dimensiune: {selectedBackup.size_mb.toFixed(1)} MB
                  </p>
                </div>
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Avertisment</AlertTitle>
                  <AlertDescription>
                    Toate datele curente vor fi pierdute și înlocuite cu cele din backup.
                    Această acțiune nu poate fi anulată.
                  </AlertDescription>
                </Alert>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
                Anulează
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => selectedBackup && restoreBackup(selectedBackup)}
              >
                Restaurează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};